<template>
  <card-dialog
    v-model="modalVisible"
    fullscreen
    persistent
    prevent-close
    @close="anyProgressMade ? closeConfirmationDialogVisible = true : modalVisible = false"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div v-if="editMode === 'addProgram'">
          {{ $t('training_programs.dialog.add_new_training_program') }}
        </div>
        <div v-else-if="editMode === 'editProgram'">
          {{ $t('training_programs.dialog.edit_training_program') }}
        </div>
        <div v-else-if="editMode === 'launchProgram'">
          {{ $t('training_programs.dialog.launch_training_program') }}
        </div>
        <div v-else-if="editMode === 'manageInstance'">
          {{ $t('training_programs.dialog.manage_training_program') }}
        </div>
      </v-card-title>
    </template>
    <v-card-text
      class="pa-0"
    >
      <validation-observer ref="validation">
        <v-row
          class="fill-height"
          no-gutters
        >
          <v-col
            style="flex: 0 0 380px"
            class="fill-height overflow-auto pa-6 pt-8"
          >
            <div v-if="editMode === 'addProgram' || editMode === 'editProgram'">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="programFormData.name"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.program_name')"
                  outlined
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.program_name')"
                />
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-textarea
                  v-model="programFormData.description"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.description')"
                  outlined
                  rows="3"
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.description')"
                />
              </validation-provider>
            </div>

            <div
              v-else-if="editMode === 'launchProgram'"
              class="mb-6"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="trainingProgramInstance.name"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.program_name')"
                  outlined
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.program_name')"
                />
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-textarea
                  v-model="trainingProgramInstance.description"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.description')"
                  outlined
                  rows="3"
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.description')"
                />
              </validation-provider>
            </div>

            <div
              v-else-if="editMode === 'manageInstance' && trainingProgramInstance.data"
              class="mb-6"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-text-field
                  v-model="trainingProgramInstance.name"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.program_name')"
                  outlined
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.program_name')"
                />
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-textarea
                  v-model="trainingProgramInstance.description"
                  :error-messages="errors"
                  :label="$t('training_programs.dialog.labels.description')"
                  outlined
                  rows="3"
                  dense
                  :placeholder="$t('training_programs.dialog.placeholders.description')"
                />
              </validation-provider>
            </div>

            <v-btn-toggle
              v-if="editMode === 'addProgram'"
              v-model="startProgramInstance"
              color="primary"
              mandatory
              dense
              class="v-btn-toggle--block mb-6"
            >
              <v-tooltip
                top
                max-width="400px"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    :value="false"
                    class="text-none text-caption"
                    @click="startProgramInstance = false"
                    v-on="on"
                  >
                    {{ $t('training_programs.dialog.save_as_template') }}
                  </v-btn>
                </template>
                <span>{{ $t('training_programs.dialog.save_as_template_description') }}</span>
              </v-tooltip>
              <v-tooltip
                top
                max-width="400px"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    :value="true"
                    class="text-none text-caption"
                    @click="startProgramInstance = true"
                    v-on="on"
                  >
                    {{ $t('training_programs.dialog.save_launch') }}
                  </v-btn>
                </template>
                <span>{{ $t('training_programs.dialog.save_launch_description') }}</span>
              </v-tooltip>
            </v-btn-toggle>

            <v-menu
              v-if="editMode === 'launchProgram'"
              v-model="startDatePickerVisible"
              :close-on-content-click="false"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="startDate ? $dayjs(startDate).format('dddd, ll') : null"
                  :label="$t('training_programs.dialog.start_date')"
                  outlined
                  dense
                  persistent-hint
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                :first-day-of-week="1"
                @input="startDatePickerVisible = false"
              />
            </v-menu>

            <v-divider class="mb-3" />

            <v-row
              no-gutters
              align="center"
              class="mb-3"
            >
              <v-col>
                <div class="text-subtitle-1">
                  {{ $t('training_programs.dialog.events') }}
                </div>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  color="grey darken-2"
                  class="text-none mr-1"
                  small
                  text
                  @click="addEvent()"
                >
                  <v-icon
                    x-small
                    left
                  >
                    fal fa-plus
                  </v-icon>
                  {{ $t('training_programs.dialog.add_event') }}
                </v-btn>
                <v-btn
                  v-if="selectedEventInternalIds.length > 1"
                  color="primary"
                  class="text-none"
                  small
                  text
                  @click="initCopyEvents()"
                >
                  <v-icon
                    x-small
                    left
                  >
                    fal fa-copy
                  </v-icon>
                  {{ $t('training_programs.copy') }}
                </v-btn>
              </v-col>
            </v-row>

            <v-alert
              v-if="events.length === 0"
              type="info"
              color="grey"
              outlined
              dense
              class="text-caption"
            >
              {{ $t('training_programs.dialog.add_event_description') }}
            </v-alert>

            <div style="padding-top: 1px">
              <training-program-event-list-card
                v-for="(event, index) in events"
                :key="'event_' + event.internal_id"
                :event="event"
                :index="index"
                :selected="selectedEventInternalIds.indexOf(event.internal_id) > -1"
                :start-instance="startProgramInstance"
                :start-date="events[0].date"
                @click.exact.native="selectEvent(event)"
                @click.shift.native="selectEvent(event, true)"
                @click.ctrl.native="selectEvent(event, true)"
                @click.meta.native="selectEvent(event, true)"
              />
            </div>

            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <input
                type="hidden"
                :value="events.length > 0 ? true : undefined"
              >
              <div
                v-if="errors.length > 0"
                class="error--text my-4 text-body-1"
              >
                {{ $t('training_programs.dialog.at_least_one_event') }}
              </div>
            </validation-provider>

            <validation-provider
              rules="required"
            >
              <input
                type="hidden"
                :value="allEventsValid > 0 ? true : undefined"
              >
            </validation-provider>
          </v-col>
          <v-divider vertical />
          <v-col
            class="fill-height"
          >
            <div class="d-flex flex-column no-gutters fill-height">
              <div class="col col-auto pa-3">
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col cols="auto">
                    <v-btn
                      icon
                      small
                      @click="$refs.programCalendar.prev()"
                    >
                      <v-icon
                        small
                        x-small
                        v-text="$vuetify.icons.values.prev"
                      />
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="$refs.programCalendar"
                    cols="auto"
                    class="px-4 text-body-1"
                  >
                    {{ $dayjs($refs.programCalendar.lastStart.date).format('ll') }} -
                    {{ $dayjs($refs.programCalendar.lastEnd.date).format('ll') }}
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      icon
                      small
                      @click="$refs.programCalendar.next()"
                    >
                      <v-icon
                        x-small
                        v-text="$vuetify.icons.values.next"
                      />
                    </v-btn>
                  </v-col>
                  <v-spacer />
                  <v-col
                    cols="auto"
                    class="pr-6"
                  >
                    <v-switch
                      v-model="showOtherEvents"
                      dense
                      hide-details
                      class="pa-0 ma-0"
                    >
                      <template v-slot:label>
                        <span class="grey--text text-body-1">{{ $t('training_programs.dialog.show_other_events') }}</span>
                      </template>
                    </v-switch>
                  </v-col>
                  <v-col cols="auto">
                    <v-btn-toggle
                      v-model="calendarView"
                      mandatory
                      dense
                      class="v-btn-toggle--block rounded"
                    >
                      <v-btn
                        small
                        class="text-none"
                        value="week"
                      >
                        {{ $t('events.calendar.option.weekly') }}
                      </v-btn>
                      <v-btn
                        small
                        class="text-none"
                        value="month"
                      >
                        {{ $t('events.calendar.option.monthly') }}
                      </v-btn>
                    </v-btn-toggle>
                  </v-col>
                </v-row>
              </div>
              <div style="height: 2px">
                <v-progress-linear
                  v-show="currentEvents.loading || loadingTrainingProgram"
                  indeterminate
                  height="2px"
                />
              </div>
              <div
                class="col overflow-x-hidden fill-height pa-0"
              >
                <v-calendar
                  ref="programCalendar"
                  v-model="calendarDay"
                  :events="calendarEvents"
                  :first-interval="user.organisation_settings.has_timezone_management ? 0 : 6"
                  :interval-count="user.organisation_settings.has_timezone_management ? 24 : 17"
                  :weekdays="[1, 2, 3, 4, 5, 6, 0]"
                  :type="calendarView"
                  @change="fetchEvents"
                  @mousedown:event="startDrag"
                  @mousedown:time="startTime"
                  @mouseup:time="endDrag"
                  @mousemove:time="mouseMove"
                  @mouseleave.native="cancelDrag"
                  @click:day="calendarDay = $event.date; calendarView = 'week'"
                  @click:date="calendarDay = $event.date; calendarView = 'week'"
                >
                  <template v-slot:event="{ event, timed }">
                    <div
                      class="v-event-draggable"
                    >
                      <div>
                        <strong>{{ event.name }}</strong>, {{ $dayjs(event.start).format(getDateTimeFormatForUser('HH:mm')) }} -
                        {{ $dayjs(event.end).format(getDateTimeFormatForUser('HH:mm')) }}
                      </div>
                      <div v-if="event.teams && event.teams.length > 0">
                        <div
                          v-for="team in event.teams"
                          :key="event.id + '_' + team.id"
                          class="opacity--08"
                        >
                          &middot; {{ team.name }}
                        </div>
                      </div>
                      <div v-else-if="event.team_ids && event.team_ids.length > 0">
                        <div
                          v-for="teamId in event.team_ids"
                          :key="event.id + '_' + teamId"
                          class="opacity--08"
                        >
                          &middot; {{ teamSelectItems.find(t => t.id === teamId) ? teamSelectItems.find(t => t.id === teamId).name : '' }}
                        </div>
                      </div>
                    </div>
                    <div
                      v-if="timed"
                      class="v-event-drag-bottom"
                      @mousedown.stop="extendBottom(event)"
                    />
                  </template>
                </v-calendar>
              </div>
            </div>
          </v-col>
          <v-divider
            v-if="selectedEventInternalId"
            vertical
          />
          <v-col
            v-if="selectedEventInternalId"
            class="fill-height py-6 px-3 pb-0"
            lg="3"
          >
            <div class="d-flex flex-column no-gutters fill-height">
              <div class="col col-auto pa-3">
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col>
                    <subtitle>
                      {{ $t('event.label.title') }} #{{ events.findIndex(e => e.internal_id === selectedEventInternalId) + 1 }}
                    </subtitle>
                  </v-col>
                  <v-col
                    cols="auto"
                    class="text-caption"
                  >
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          color="grey darken-1"
                          icon
                          :disabled="events.findIndex(e => e.internal_id === selectedEventInternalId) === 0"
                          v-on="on"
                          @click="previousEvent"
                        >
                          <v-icon small>
                            far fa-arrow-left
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('training_programs.dialog.previous_event') }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          color="grey darken-1"
                          icon
                          :disabled="events.findIndex(e => e.internal_id === selectedEventInternalId) === (events.length - 1)"
                          class="mr-3"
                          v-on="on"
                          @click="nextEvent"
                        >
                          <v-icon small>
                            far fa-arrow-right
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('training_programs.dialog.next_event') }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          color="grey darken-1"
                          icon
                          v-on="on"
                          @click="duplicateEvent()"
                        >
                          <v-icon small>
                            far fa-copy
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('training_programs.dialog.duplicate') }}</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn
                          color="grey darken-1"
                          icon
                          class="mr-1"
                          v-on="on"
                          @click="removeEvent"
                        >
                          <v-icon small>
                            far fa-trash
                          </v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('training_programs.dialog.remove') }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
              </div>
              <v-divider />
              <div
                v-if="selectedEvent"
                class="pa-4 col overflow-y-auto overflow-x-hidden pa-3 mt-2 pb-12"
              >
                <training-program-event-form
                  ref="trainingProgramEventForm"
                  :event.sync="selectedEvent"
                  :starts-program="startProgramInstance"
                  :start-date="events[0].date"
                  @apply-all="applyToAllEvents"
                />
              </div>
            </div>
          </v-col>
        </v-row>
      </validation-observer>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4 grey-bg">
        <v-btn
          color="primary"
          large
          :loading="submitting"
          class="text-none"
          @click="submit()"
        >
          {{ editMode === 'addProgram' ? (startProgramInstance ? $t('training_programs.dialog.submit.launch_events') : $t('training_programs.dialog.submit.save_training_program')) : ( editMode === 'launchProgram' ? $t('training_programs.dialog.submit.launch_events') : ( editMode === 'manageInstance' ? $t('training_programs.dialog.submit.update_events') : $t('training_programs.dialog.submit.save_training_program')) ) }}
        </v-btn>
        <v-btn
          color="grey"
          text
          class="text-none"
          @click="anyProgressMade ? closeConfirmationDialogVisible = true : modalVisible = false"
        >
          {{ $t('training_programs.dialog.submit.cancel') }}
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="programFormData.id || trainingProgramInstance.id"
          color="error"
          text
          class="text-none"
          @click="startDelete()"
        >
          <v-icon
            left
            x-small
          >
            far fa-trash
          </v-icon>
          {{ $t('training_programs.dialog.submit.delete') }}
        </v-btn>
        <v-btn
          v-if="editMode === 'editProgram'"
          color="grey darken-2"
          text
          class="text-none"
          :loading="duplicatingProgram"
          @click="duplicateProgram()"
        >
          <v-icon
            left
            x-small
          >
            far fa-copy
          </v-icon>
          {{ $t('button.duplicate') }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirmed="deleteProgram()"
    />

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="modalVisible = false"
    />

    <card-dialog
      v-model="launchEventsDialog.visible"
      :title="$t('training_programs.dialog.launch_dialog.title')"
      max-width="600px"
      prevent-close
      @close="launchEventsDialog.visible = false; modalVisible = false"
    >
      <v-card-text class="pa-6">
        <validation-observer ref="launchDialogValidation">
          <v-alert
            outlined
            color="primary"
            class="text-body-1 mb-6"
          >
            {{ $t('training_programs.dialog.launch_dialog.description') }}
          </v-alert>

          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-clock
            </v-icon>
            {{ $t('training_programs.dialog.launch_dialog.start_date') }}
          </subtitle>

          <v-row no-gutters>
            <v-col cols="5">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-menu>
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      :value="launchEventsDialog.applyAll.start_date ? $dayjs(launchEventsDialog.applyAll.start_date).format('L') : ''"
                      :error-messages="errors"
                      :label="$t('picker.date.title')"
                      outlined
                      dense
                      readonly
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="launchEventsDialog.applyAll.start_date"
                    :first-day-of-week="1"
                    no-title
                  />
                </v-menu>
              </validation-provider>
            </v-col>
          </v-row>

          <div v-if="hasTimezoneManagement">
            <v-row
              no-gutters
              :class="{ 'mb-3': launchEventsDialog.applyTimezone, 'mb-6': !launchEventsDialog.applyTimezone }"
            >
              <v-col cols="auto">
                <subtitle>
                  <v-icon
                    left
                    small
                  >
                    fal fa-clock
                  </v-icon>
                  {{ $t('training_programs.dialog.launch_dialog.timezone_all_events') }}
                </subtitle>
              </v-col>
              <v-col
                cols="auto"
                class="pl-4"
              >
                <v-switch
                  v-model="launchEventsDialog.applyTimezone"
                  class="pa-0 ma-0"
                  hide-details
                />
              </v-col>
            </v-row>

            <div
              v-if="launchEventsDialog.applyTimezone"
            >
              <validation-provider
                v-slot="{ errors }"
                rules="required"
              >
                <v-autocomplete
                  v-model="launchEventsDialog.applyAll.tz"
                  outlined
                  :items="timezones"
                  item-value="tzCode"
                  item-text="name"
                  dense
                  :placeholder="$t('dropdown.current_timezone.placeholder')"
                  :error-messages="errors"
                />
              </validation-provider>
            </div>
          </div>

          <v-row
            no-gutters
            :class="{ 'mb-3': launchEventsDialog.applyStartTime, 'mb-6': !launchEventsDialog.applyStartTime }"
          >
            <v-col cols="auto">
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-clock
                </v-icon>
                {{ $t('training_programs.dialog.launch_dialog.start_time_all_events') }}
              </subtitle>
            </v-col>
            <v-col
              cols="auto"
              class="pl-4"
            >
              <v-switch
                v-model="launchEventsDialog.applyStartTime"
                class="pa-0 ma-0"
                hide-details
              />
            </v-col>
          </v-row>

          <div
            v-if="launchEventsDialog.applyStartTime"
            style="width: 200px"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-combobox
                v-model="launchEventsDialog.applyAll.start_time"
                :error-messages="errors"
                dense
                outlined
                :items="timeItems"
              />
            </validation-provider>
          </div>

          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-user-group
            </v-icon>
            {{ $t('add_new.team') }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-autocomplete
              v-model="launchEventsDialog.applyAll.team_ids"
              outlined
              :items="teamSelectItems"
              item-value="id"
              item-text="name"
              dense
              multiple
              :error-messages="errors"
              :placeholder="$t('select_team.placeholder')"
            />
          </validation-provider>

          <v-row
            no-gutters
            class="mb-3"
            align="center"
          >
            <v-col>
              <subtitle>
                <v-icon
                  left
                  small
                >
                  fal fa-user
                </v-icon>
                {{ $t("dropdown.players.title") }}
              </subtitle>
            </v-col>
            <v-col
              class="grey--text text-caption"
              cols="auto"
            >
              {{ $t('added_players', {count: launchEventsDialog.applyAll.player_ids.length }) }}
            </v-col>
          </v-row>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-autocomplete
              v-model="launchEventsDialog.applyAll.player_ids"
              outlined
              :items="launchEventsDialog.players.data"
              item-value="id"
              item-text="name"
              dense
              multiple
              :chips="false"
              :error-messages="errors"
              clearable
              :placeholder="$t('dropdown.players.placeholder')"
            >
              <template v-slot:selection="{ item, index }">
                <span
                  v-if="index > 0 && index < 3"
                  class="mr-1"
                >, </span>
                <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
                <span
                  v-else-if="index === 3"
                  class="grey--text text-caption ml-1"
                >
                  {{ $t('event.and_others') }}
                </span>
              </template>
              <template v-slot:item="{ item, on }">
                <v-list-item-action>
                  <v-simple-checkbox
                    :value="launchEventsDialog.applyAll.player_ids.indexOf(item.id) > -1"
                    :ripple="false"
                    v-on="on"
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ item.last_name }}, {{ item.first_name }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    <span>{{ item.team.short_name }}</span>
                    <span
                      v-if="item.player_positions && item.player_positions.length > 0"
                    >&middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </validation-provider>

          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-user
            </v-icon>
            {{ $t("eventDetails.reservations.coaches") }}
          </subtitle>

          <validation-provider
            v-slot="{ errors }"
          >
            <v-autocomplete
              v-model="launchEventsDialog.applyAll.coach_ids"
              outlined
              :items="launchEventsDialog.coaches.data"
              :loading="launchEventsDialog.coaches.loading"
              item-value="id"
              :item-text="(coach) => coach.first_name + ' ' + coach.last_name"
              dense
              multiple
              clearable
              :error-messages="errors"
              :placeholder="$t('eventDetails.reservations.coaches.placeholder')"
            />
          </validation-provider>

          <subtitle class="mb-3">
            <v-icon
              left
              small
            >
              fal fa-location-dot
            </v-icon>
            {{ $t('dropdown.location.title') }}
          </subtitle>

          <v-autocomplete
            v-model="launchEventsDialog.applyAll.location_id"
            outlined
            :items="launchEventsDialog.location.items"
            item-value="id"
            item-text="name"
            dense
            :placeholder="$t('dropdown.location.placeholder')"
          />
        </validation-observer>
      </v-card-text>

      <template v-slot:actions>
        <v-card-actions class="pa-4 grey-bg">
          <v-spacer />
          <v-btn
            color="primary"
            @click="submitLaunchEventDialog()"
          >
            {{ $t('training_programs.dialog.launch_dialog.apply') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>

    <v-dialog
      v-model="deleteInstanceDialog.visible"
      max-width="400px"
    >
      <v-card>
        <v-card-title class="text-body-2 px-5 py-4 grey--text text--darken-2">
          {{ $t('training_programs.dialog.delete_dialog.what_delete') }}
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0">
          <v-list
            dense
            class="pa-0"
          >
            <v-list-item-group
              v-model="deleteInstanceDialog.mode"
              color="primary"
              mandatory
            >
              <v-list-item
                value="all"
                input-value="all"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t('training_programs.dialog.delete_dialog.activated_programs_all_events') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="deleteInstanceDialog.mode === 'all'">
                  <v-icon small>
                    far fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-list-item
                value="future_events"
                input-value="future_events"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t('training_programs.dialog.delete_dialog.activated_programs_future_events') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="deleteInstanceDialog.mode === 'future_events'">
                  <v-icon small>
                    far fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
              <v-list-item
                value="program"
                input-value="program"
              >
                <v-list-item-content>
                  <v-list-item-title class="text-body-1">
                    {{ $t('training_programs.dialog.delete_dialog.just_program_leave_events') }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="deleteInstanceDialog.mode === 'program'">
                  <v-icon small>
                    far fa-check
                  </v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list-item-group>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :loading="submitting || deleting"
            @click="submitDeleteInstance()"
          >
            {{ $t('training_programs.dialog.submit.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <card-dialog
      v-model="duplicateEventMediaDialog"
      :title="$t('training_programs.dialog.copy_dialog.copy_media')"
      max-width="400px"
    >
      <v-card-text class="pa-6">
        {{ $t('training_programs.dialog.copy_dialog.description') }}
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4 grey-bg">
          <v-spacer />
          <v-btn
            color="grey darken-2"
            text
            class="text-none"
            @click="duplicateEvent(false)"
          >
            {{ $t('training_programs.dialog.copy_dialog.submit_no') }}
          </v-btn>
          <v-btn
            color="primary"
            class="text-none"
            @click="duplicateEvent(true)"
          >
            {{ $t('training_programs.dialog.copy_dialog.submit_yes') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>

    <card-dialog
      v-model="copyEventsDialog.visible"
      title="Copy events"
      max-width="400px"
    >
      <v-card-text class="pa-6">
        <div class="mb-3">
          {{ $t('training_programs.copy.select_start_date') }}
        </div>
        <v-row no-gutters>
          <v-col cols="7">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
            >
              <v-menu>
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :value="copyEventsDialog.newDate ? $dayjs(copyEventsDialog.newDate).format('L') : ''"
                    :error-messages="errors"
                    :label="$t('picker.date.title')"
                    outlined
                    dense
                    readonly
                    hide-details
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="copyEventsDialog.newDate"
                  :first-day-of-week="1"
                  no-title
                />
              </v-menu>
            </validation-provider>
          </v-col>
          <v-col class="pl-4">
            <validation-provider
              v-slot="{ errors }"
              rules="required|time"
            >
              <v-combobox
                v-model="copyEventsDialog.newStartTime"
                :error-messages="errors"
                dense
                outlined
                :label="$t('picker.time.title')"
                hide-details
                :items="timeItems"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <v-switch
          v-if="selectedEventsForCopy.some(e => e.media.length > 0)"
          v-model="copyEventsDialog.copyMedia"
          dense
          :label="$t('training_programs.copy.also_copy_media')"
        />
        <v-divider class="my-5" />
        <div
          v-for="event in selectedEventsForCopy"
          :key="'copy' + event.internal_id"
          class="rounded-lg pa-4 mb-1 grey lighten-4 grey--text text--darken-2"
        >
          <v-row
            no-gutters
            class="text-body-1 flex-nowrap"
            style="min-width: 0"
          >
            <v-col class="text-truncate pr-2">
              <div
                v-if="!event.title"
                class="font-weight-medium"
              >
                {{ $t('event.label.title') }}
              </div>
              <div
                v-else
                class="font-weight-medium text-truncate"
              >
                {{ event.title }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="text-caption"
            >
              <div>
                {{ $dayjs(event.start_at).format(getDateTimeFormatForUser('ddd, L - HH:mm')) }}
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <template v-slot:actions>
        <v-card-actions class="pa-4 grey-bg">
          <v-spacer />
          <v-btn
            color="grey darken-2"
            text
            class="text-none"
            @click="copyEventsDialog.visible = false"
          >
            {{ $t('button.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            class="text-none"
            @click="copyEvents()"
          >
            {{ $t('training_programs.copy') }}
          </v-btn>
        </v-card-actions>
      </template>
    </card-dialog>
  </card-dialog>
</template>

<script>
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { EventBus } from '@/plugins/bus'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog'
import Subtitle from '@/components/helpers/Subtitle'
import { cloneDeep, difference, intersection, union } from 'lodash'
import { TrainingProgramApiService } from '@/data/services/TrainingProgramApiService'
import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventType, EventTypes } from '@/data/models'
import TrainingProgramEventForm from '@/components/training-programs/TrainingProgramEventForm.vue'
import { CoachApiService, EventApiService, LocationApiService, PlayerApiService } from '@/data/services'
import dayjs from 'dayjs'
import TrainingProgramEventListCard from '@/components/training-programs/TrainingProgramEventListCard.vue'
import { MediaApiService } from '@/data/services/MediaApiService'
import timezones from 'timezones-list'
import { getDateTimeFormatForUser } from '@/utils/helpers'

const emptyProgramFormData = {
  id: undefined,
  name: '',
  description: '',
  force_week_start_day: null,
  start_date: null
}

const emptyEventData = {
  id: null,
  internal_id: null,
  title: '',
  description: '',
  date: '',
  start_time: '',
  end_time: '',
  is_description_players_visible: false,
  is_player_diagnostic_entry_allowed: false,
  player_can_view_training_protocol: true,
  event_type: EventType.TRAINING,
  team_ids: [],
  player_ids: [],
  coach_ids: [],
  is_open_application: false,
  max_participants: null,
  is_open_application_team_constrained: true,
  open_application_start_days_before: null,
  open_application_start_hours_before: null,
  open_application_end_days_before: null,
  open_application_end_hours_before: null,
  open_application_start_at: null,
  open_application_end_at: null,
  location_id: null,
  protocol_id: null,
  training_protocol_id: null,
  media: []
}

export default {
  name: 'TrainingProgramFormDialog',
  components: {
    TrainingProgramEventListCard,
    TrainingProgramEventForm,
    CardDialog,
    Subtitle,
    DeleteConfirmationDialog
  },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      currentEvents: {
        data: [],
        loading: false
      },
      calendarDay: dayjs().format('YYYY-MM-DD'),
      calendarView: 'week',
      showOtherEvents: true,
      dragEvent: null,
      dragStartMouseOffset: null,
      dragEventOriginalStart: null,

      programFormData: cloneDeep(emptyProgramFormData),
      loadingTrainingProgram: false,
      startDate: null,
      startProgramInstance: false,

      events: [],
      selectedEventInternalIds: [],

      startDatePickerVisible: false,
      submitting: false,

      trainingProgramInstance: {
        id: undefined,
        loading: false,
        data: undefined,
        name: null,
        description: null
      },

      anyProgressMade: false,
      closeConfirmationDialogVisible: false,

      timeItems: [],
      launchEventsDialog: {
        visible: false,
        players: {
          data: [],
          loading: false
        },
        coaches: {
          data: [],
          loading: false
        },
        location: {
          items: [],
          itemsLoading: false
        },
        applyStartTime: false,
        applyTimezone: false,
        applyAll: {
          team_ids: [],
          player_ids: [],
          coach_ids: [],
          location_id: null,
          start_date: null,
          start_time: null,
          tz: null
        }
      },

      deleteInstanceDialog: {
        visible: false,
        mode: 'program',
        submitting: false
      },

      duplicateEventMediaDialog: false,

      copyEventsDialog: {
        visible: false,
        newDate: null,
        newStartTime: null,
        copyMedia: true
      },

      duplicatingProgram: false

    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    selectedEvent: {
      get: function () {
        return this.events.find(e => e.internal_id === this.selectedEventInternalId)
      },
      set: function (value) {
        const index = this.events.findIndex(e => e.internal_id === this.selectedEventInternalId)

        this.events.splice(index, 1, value)
      }
    },
    editMode() {
      if (this.trainingProgramInstance.id) return 'manageInstance'

      if (this.programFormData.id && this.startProgramInstance) return 'launchProgram'

      if (this.programFormData.id) return 'editProgram'

      return 'addProgram'
    },
    allEventsValid() {
      return this.events.every(e => this.isEventValid(e))
    },
    formValid() {
      return this.allEventsValid && !!this.programFormData.name && !!this.programFormData.description
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    calendarEvents() {
      if (this.currentEvents.loading || this.loadingTrainingProgram || this.trainingProgramInstance.loading) return []

      let events = []

      if (this.showOtherEvents) {
        events = this.currentEvents.data.filter(event => {
          return !this.events.find(e => e.id === event.id)
        }).map(event => {
          return {
            ...event,
            start: dayjs(event.start_at).toDate(),
            end: dayjs(event.end_at).toDate(),
            date: dayjs(event.start_at).format('YYYY-MM-DD'),
            start_time: dayjs(event.start_at).format(getDateTimeFormatForUser('HH:mm')),
            end_time: dayjs(event.end_at).format(getDateTimeFormatForUser('HH:mm')),
            name: event.title,
            color: 'grey lighten-1',
            timed: true
          }
        })
      }

      events = [...events, ...this.events.map((event, index) => {
        return {
          ...event,
          internal_id: event.internal_id,
          name: event.title || 'Event #' + (index + 1),
          color: this.selectedEventInternalIds.indexOf(event.internal_id) > -1 ? 'primary' : this.eventTypes[event.event_type].color,
          timed: true
        }
      })]

      return events
    },
    teamSelectItems() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    selectedEventInternalId() {
      if (this.selectedEventInternalIds.length === 1) return this.selectedEventInternalIds[0]

      return undefined
    },
    selectedEvents() {
      return this.selectedEventInternalIds.map(internalId => {
        return this.events.find((event) => event.internal_id === internalId)
      })
    },
    selectedEventsForCopy() {
      if (!this.copyEventsDialog.visible || !this.copyEventsDialog.newStartTime || !this.copyEventsDialog.newDate) return []

      const originalStart = dayjs(this.selectedEvents[0].start_at)

      const offset = dayjs(this.copyEventsDialog.newDate + ' ' + dayjs(this.copyEventsDialog.newStartTime, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss')).diff(originalStart, 'minute')

      return this.selectedEvents.map(event => {
        const startAt = dayjs(event.start_at)
        const endAt = dayjs(event.end_at)

        return {
          ...event,
          date: startAt.add(offset, 'minute').format('YYYY-MM-DD'),
          start_at: startAt.add(offset, 'minute').toISOString(),
          end_at: endAt.add(offset, 'minute').toISOString(),
          start: startAt.add(offset, 'minute').toDate(),
          end: endAt.add(offset, 'minute').toDate(),
          start_time: startAt.add(offset, 'minute').format('HH:mm'),
          end_time: endAt.add(offset, 'minute').format('HH:mm')
        }
      })
    },
    hasTimezoneManagement() {
      return this.user.organisation_settings.has_timezone_management
    }
  },
  watch: {
    visible(newValue) {
      if (!newValue) return

      setTimeout(() => {
        this.$refs.validation.reset()

        this.fetchEvents()
      }, 0)

      setTimeout(() => {
        if (this.$refs.programCalendar && this.$refs.programCalendar.$children[0]) { this.$refs.programCalendar.$children[0].init() }
      }, 500)
    },
    'selectedEvent.date': function (newDate) {
      if (!newDate) return

      this.resortEvents()

      this.calendarDay = newDate
    },
    'selectedEvent.start_time': function () {
      this.resortEvents()
    },
    startDate: function (newStartDate, oldStartDate) {
      if (!newStartDate || !oldStartDate) return

      newStartDate = dayjs(newStartDate)
      oldStartDate = dayjs(oldStartDate)

      const diff = newStartDate.diff(oldStartDate, 'day')

      this.events = this.events.map(e => {
        const startAt = dayjs(e.start_at)
        const endAt = dayjs(e.end_at)

        return {
          ...e,
          date: startAt.add(diff, 'day').format('YYYY-MM-DD'),
          start_at: startAt.add(diff, 'day').toISOString(),
          end_at: endAt.add(diff, 'day').toISOString(),
          start: startAt.add(diff, 'day').toDate(),
          end: endAt.add(diff, 'day').toDate()
        }
      })

      this.calendarDay = newStartDate.format('YYYY-MM-DD')
    },
    'launchEventsDialog.applyAll.team_ids': function (oldValue, newValue) {
      const addedTeamIds = difference(oldValue, newValue)
      const removedTeamIds = difference(newValue, oldValue)

      const removePlayerIds = this.launchEventsDialog.players.data.filter(p => removedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)
      const addPlayerIds = this.launchEventsDialog.players.data.filter(p => addedTeamIds.indexOf(p.team_id) > -1).map(p => p.id)

      this.launchEventsDialog.applyAll.player_ids = union(difference(this.launchEventsDialog.applyAll.player_ids, removePlayerIds), addPlayerIds)

      const removeCoachIds = this.launchEventsDialog.coaches.data.filter(c => intersection(c.teams.map(t => t.id), removedTeamIds).length > 0).map(c => c.id)
      const addCoachIds = this.launchEventsDialog.coaches.data.filter(c => intersection(c.teams.map(t => t.id), addedTeamIds).length > 0).map(c => c.id)

      this.launchEventsDialog.applyAll.coach_ids = union(difference(this.launchEventsDialog.applyAll.coach_ids, removeCoachIds), addCoachIds)
    },
    events: {
      deep: true,
      handler: function () {
        this.anyProgressMade = true
      }
    }
  },
  created() {
    this.eventTypes = EventTypes

    this.weekDays = [1, 2, 3, 4, 5, 6, 7].map(day => {
      return {
        text: this.$dayjs().day(day).format('dddd'),
        value: day
      }
    })

    this.timezones = timezones

    EventBus.$on(this.$events.OPEN_DIALOG_TRAINING_PROGRAM_FORM, () => {
      this.modalVisible = true
      this.programFormData = cloneDeep(emptyProgramFormData)
      this.trainingProgramInstance.id = undefined
      this.trainingProgramInstance.data = undefined
      this.trainingProgramInstance.name = null
      this.trainingProgramInstance.description = null
      this.startProgramInstance = false
      this.selectedEventInternalIds = []
      this.events = []
      this.calendarDay = dayjs().format('YYYY-MM-DD')
      this.calendarView = 'week'
      this.showOtherEvents = true

      this.$nextTick(() => {
        this.anyProgressMade = false
      })
    })

    EventBus.$on(this.$events.EDIT_TRAINING_PROGRAM, (programId) => {
      this.trainingProgramInstance.id = undefined
      this.trainingProgramInstance.data = undefined
      this.trainingProgramInstance.name = null
      this.trainingProgramInstance.description = null

      this.startDate = null
      this.calendarView = 'week'
      this.showOtherEvents = true

      this.loadTrainingProgram(programId)

      this.modalVisible = true

      this.$nextTick(() => {
        this.anyProgressMade = false
      })
    })

    EventBus.$on(this.$events.LAUNCH_TRAINING_PROGRAM, (programId) => {
      this.trainingProgramInstance.id = undefined
      this.trainingProgramInstance.data = undefined
      this.trainingProgramInstance.name = null
      this.trainingProgramInstance.description = null
      this.calendarView = 'week'

      this.loadTrainingProgram(programId, true)

      this.modalVisible = true

      this.$nextTick(() => {
        this.anyProgressMade = false
      })
    })

    EventBus.$on(this.$events.EDIT_TRAINING_PROGRAM_INSTANCE, (instanceId) => {
      this.trainingProgramInstance.id = instanceId
      this.calendarView = 'week'
      this.showOtherEvents = true

      this.loadTrainingProgramInstance(instanceId)

      this.modalVisible = true

      this.$nextTick(() => {
        this.anyProgressMade = false
      })
    })

    const items = []

    let startTime = dayjs().startOf('day')
    const day = startTime.day()

    while (startTime.day() === day) {
      items.push(startTime.format(getDateTimeFormatForUser('HH:mm')))
      startTime = startTime.add(15, 'm')
    }

    this.timeItems = items
  },
  methods: {
    getDateTimeFormatForUser,
    async fetchEvents(dates = null) {
      this.currentEvents.loading = true

      const from = dates ? dates.start.date : this.$refs.programCalendar.lastStart.date
      const to = dates ? dates.end.date : this.$refs.programCalendar.lastEnd.date

      try {
        const response = await EventApiService.getAll({
          from: dayjs(from).format('YYYY-MM-DD'),
          to: dayjs(to).format('YYYY-MM-DD'),
          compact: true
        })

        this.currentEvents.data = response.data
        // eslint-disable-next-line no-useless-catch
      } catch (e) {
        throw e
      } finally {
        this.currentEvents.loading = false
      }
    },
    startDrag({
                event,
                timed
              }) {
      if ((this.calendarView === 'month' && event) || (event && timed)) {
        this.dragEvent = event
        this.dragStartMouseOffset = null
      }
    },
    toTime(tms) {
      return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
    },
    roundTime(time, down = true) {
      const roundTo = 15 // minutes
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - time % roundDownTime
        : time + (roundDownTime - (time % roundDownTime))
    },
    startTime(tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragStartMouseOffset === null) {
        const start = this.dragEvent.start

        this.dragStartMouseOffset = mouse - start
      } else {
        this.addEvent(tms)
      }
    },
    extendBottom(event) {
      this.dragEvent = event
      this.dragEventOriginalStart = event.start
    },
    mouseMove(tms) {
      if (this.dragEvent && !this.events.find(e => e.internal_id === this.dragEvent.internal_id)) return

      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragStartMouseOffset !== null) {
        const start = this.dragEvent.start
        const end = this.dragEvent.end
        const duration = end - start
        const newStartTime = mouse - this.dragStartMouseOffset
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + duration

        this.dragEvent.start = newStart
        this.dragEvent.end = newEnd

        const eventIndex = this.events.findIndex(e => e.internal_id === this.dragEvent.internal_id)
        const event = this.events[eventIndex]

        event.start = newStart
        event.end = newEnd
      } else if (this.dragEvent && this.dragEventOriginalStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)

        const min = Math.min(mouseRounded, this.dragEventOriginalStart)
        const max = Math.max(mouseRounded, this.dragEventOriginalStart)

        this.dragEvent.start = min
        this.dragEvent.end = max

        const eventIndex = this.events.findIndex(e => e.internal_id === this.dragEvent.internal_id)
        const event = this.events[eventIndex]

        event.start = min
        event.end = max
      }
    },
    async endDrag({ nativeEvent }) {
      if (!this.dragEvent) return

      const dragEvent = this.dragEvent

      this.dragStartMouseOffset = null
      this.dragEvent = null
      this.dragEventOriginalStart = null

      const start = dayjs(dragEvent.start)
      const originalStart = dayjs(dragEvent.start_at)
      const end = dayjs(dragEvent.end)
      const originalEnd = dayjs(dragEvent.end_at)

      if (!start.isSame(originalStart, 'second') || !end.isSame(originalEnd, 'second')) {
        const eventIndex = this.events.findIndex(e => e.internal_id === dragEvent.internal_id)
        const event = this.events[eventIndex]

        event.start_at = start.toISOString()
        event.start = start.toDate()
        event.start_time = start.format('HH:mm')
        event.end_at = end.toISOString()
        event.end = end.toDate()
        event.end_time = end.format('HH:mm')
        event.date = start.format('YYYY-MM-DD')

        this.events.splice(eventIndex, 1, event)
      }

      this.showEvent({ event: dragEvent, nativeEvent })
    },
    cancelDrag() {
      this.dragStartMouseOffset = null
      this.dragEvent = null
      this.dragEventOriginalStart = null
    },
    async showEvent({ event, nativeEvent }) {
      if (this.calendarView === 'month') return

      if (nativeEvent && (nativeEvent.shiftKey || nativeEvent.ctrlKey || nativeEvent.metaKey)) {
        this.selectEventMultiple(event)
        return
      }

      if (!event.internal_id) {
        const result = await EventApiService.getEventDetails(event.id)

        const newEvent = result.data

        const start = dayjs(newEvent.start_at)
        const end = dayjs(newEvent.end_at)

        newEvent.internal_id = this.getNewId()
        newEvent.start_at = start.toISOString()
        newEvent.start = start.toDate()
        newEvent.start_time = start.format('HH:mm')
        newEvent.end_at = end.toISOString()
        newEvent.end = end.toDate()
        newEvent.end_time = end.format('HH:mm')
        newEvent.date = start.format('YYYY-MM-DD')

        this.events.push(newEvent)
        this.selectedEventInternalIds = [newEvent.internal_id]
      } else {
        this.selectedEventInternalIds = [event.internal_id]
      }
    },
    getNewId() {
      let id = 0

      for (const event of this.events) {
        if (event.internal_id > id) id = event.internal_id
      }

      return id + 1
    },
    async addEvent(tms = null) {
      const event = cloneDeep(emptyEventData)

      let startAt
      let endAt

      if (tms) {
        startAt = dayjs(tms.date).hour(tms.hour).minute((tms.minute / 15).toFixed(0) * 15).second(0)
        endAt = startAt.add(1, 'hour')
      } else {
        if (this.events.length > 0) {
          const latestEvent = this.events[this.events.length - 1]

          startAt = dayjs(latestEvent.start_at).add(1, 'day')
          endAt = dayjs(latestEvent.end_at).add(1, 'day')
        } else {
          startAt = dayjs().hour(12).minute(0).second(0)
          endAt = startAt.add(1, 'hour')
        }
      }

      event.start_at = startAt.toISOString()
      event.start = startAt.toDate()
      event.end_at = endAt.toISOString()
      event.end = endAt.toDate()
      event.date = startAt.format('YYYY-MM-DD')
      event.start_time = startAt.format('HH:mm')
      event.end_time = endAt.format('HH:mm')

      event.internal_id = this.getNewId()

      this.events.push(event)

      this.selectedEventInternalIds = [event.internal_id]

      this.calendarDay = event.date
    },
    async submit() {
      // if (!(await this.$refs.trainingProgramEventForm.$refs.validation.validate())) return

      if (!(await this.$refs.validation.validate())) return

      if (this.editMode === 'manageInstance' || this.editMode === 'launchProgram') {
        await this.launch()
      } else {
        await this.save()
      }
    },
    async launch() {
      this.submitting = true

      try {
        let result

        if (this.editMode === 'manageInstance') {
          result = await TrainingProgramApiService.updateInstance(this.trainingProgramInstance.data.id, {
            events: this.events.map(e => ({
              ...e,
              start_at: dayjs.tz(e.date + ' ' + dayjs(e.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), e.tz || undefined).toISOString(),
              end_at: dayjs.tz(e.date + ' ' + dayjs(e.end_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), e.tz || undefined).toISOString(),
              media_ids: e.media.map(m => m.id)
            })),
            name: this.trainingProgramInstance.name,
            description: this.trainingProgramInstance.description
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('training_programs.dialog.alerts.instance_updated'),
            color: 'success'
          })
        } else {
          result = await TrainingProgramApiService.startProgram(this.programFormData.id, {
            events: this.events.map(e => ({
              ...e,
              start_at: dayjs.tz(e.date + ' ' + dayjs(e.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), e.tz || undefined).toISOString(),
              end_at: dayjs.tz(e.date + ' ' + dayjs(e.end_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'), e.tz || undefined).toISOString(),
              media_ids: e.media.map(m => m.id)
            })),
            name: this.trainingProgramInstance.name,
            description: this.trainingProgramInstance.description
          })

          this.$store.dispatch(this.$storeActions.ALERT, {
            text: this.$t('training_programs.dialog.alerts.program_started'),
            color: 'success'
          })
        }

        EventBus.$emit(this.$events.TRAINING_PROGRAM_INSTANCE_UPDATED, result.data)

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.error_launching'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async save() {
      try {
        this.submitting = true

        const data = {
          name: this.programFormData.name,
          description: this.programFormData.description,
          events: this.events.map(e => ({
            ...e,
            media_ids: e.media.map(m => m.id)
          })),
          start: this.startProgramInstance
        }

        let result

        if (this.programFormData.id) {
          result = await TrainingProgramApiService.update(this.programFormData.id, data)
        } else {
          result = await TrainingProgramApiService.create(data)
        }

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: data.start ? this.$t('training_programs.dialog.alerts.events_launched_template_saved') : this.$t('training_programs.dialog.alerts.program_saved'),
          color: 'success'
        })

        this.$emit('refresh', result.data)

        if (this.programFormData.id) {
          EventBus.$emit(this.$events.TRAINING_PROGRAM_UPDATED, result.data)
        } else {
          EventBus.$emit(this.$events.TRAINING_PROGRAM_CREATED, result.data)
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.error_saving_program'),
          color: 'error'
        })
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteProgram() {
      if (!this.programFormData.id) return

      try {
        this.deleting = true

        await TrainingProgramApiService.delete(this.programFormData.id)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.program_deleted'),
          color: 'success'
        })

        this.deleteConfirmationDialogVisible = false

        EventBus.$emit(this.$events.TRAINING_PROGRAM_DELETED, this.programFormData)
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.error_deleting_program'),
          color: 'error'
        })
        throw e
      } finally {
        this.deleting = false
      }
    },
    async loadTrainingProgram(programId, launch = false) {
      this.loadingTrainingProgram = true

      this.startDate = null
      this.events = []
      this.selectedEventInternalIds = []

      try {
        const result = await TrainingProgramApiService.getDetails(programId)

        this.programFormData = result.data

        this.events = result.data.training_program_events.map(e => {
          const startAt = dayjs(e.original_date + ' ' + e.start_time)
          const endAt = dayjs(e.original_date + ' ' + e.end_time)

          return {
            ...e,
            id: null,
            internal_id: e.id,
            training_program_event_id: e.id,
            start: startAt.toDate(),
            end: endAt.toDate(),
            start_at: startAt.toISOString(),
            end_at: endAt.toISOString(),
            date: startAt.format('YYYY-MM-DD'),
            start_time: e.start_time ? this.$dayjs(e.start_time, 'HH:mm:ss').format('HH:mm') : null,
            end_time: e.end_time ? this.$dayjs(e.end_time, 'HH:mm:ss').format('HH:mm') : null,
            team_ids: [],
            player_ids: [],
            coach_ids: []
          }
        })

        this.startProgramInstance = launch

        if (launch) {
          this.fetchAllCoaches()
          this.fetchAllPlayers()
          this.fetchAllLocations()

          this.launchEventsDialog.applyAll.start_date = null
          this.launchEventsDialog.applyAll.start_time = null
          this.launchEventsDialog.applyAll.coach_ids = []
          this.launchEventsDialog.applyAll.player_ids = []
          this.launchEventsDialog.applyAll.team_ids = []
          this.launchEventsDialog.applyAll.location_id = null
          this.launchEventsDialog.applyAll.tz = null

          this.launchEventsDialog.applyStartTime = false
          this.launchEventsDialog.applyTimezone = false

          this.launchEventsDialog.visible = true

          this.$nextTick(() => {
            if (this.$refs.launchDialogValidation) {
              this.$refs.launchDialogValidation.reset()
            }
          })

          this.trainingProgramInstance.name = result.data.name
          this.trainingProgramInstance.description = result.data.description
        }

        this.selectedEventInternalIds = []

        this.resortEvents()

        this.$nextTick(() => {
          this.startDate = this.events[0].date
          this.calendarDay = this.events[0].date

          this.anyProgressMade = false
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.error_loading_program'),
          color: 'error'
        })
        throw e
      } finally {
        this.loadingTrainingProgram = false
      }
    },
    async loadTrainingProgramInstance(instanceId) {
      this.trainingProgramInstance.loading = true

      this.events = []
      this.selectedEventInternalIds = []
      this.startDate = null

      try {
        const result = await TrainingProgramApiService.getInstance(instanceId)

        this.trainingProgramInstance.data = result.data
        this.trainingProgramInstance.name = result.data.name
        this.trainingProgramInstance.description = result.data.description

        this.events = result.data.events.map(e => {
          const startAt = dayjs(e.start_at)
          const endAt = dayjs(e.end_at)

          return {
            ...e,
            id: e.id,
            internal_id: e.id,
            start: startAt.toDate(),
            end: endAt.toDate(),
            date: startAt.format('YYYY-MM-DD'),
            start_time: startAt.format('HH:mm'),
            end_time: endAt.format('HH:mm')
          }
        })

        this.startProgramInstance = true

        this.resortEvents()

        this.$nextTick(() => {
          this.startDate = this.events[0].date
          this.calendarDay = this.events[0].date

          this.anyProgressMade = false
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.error_loading_program'),
          color: 'error'
        })
        throw e
      } finally {
        this.trainingProgramInstance.loading = false
      }
    },
    async duplicateEvent(copyMedia = null) {
      const event = cloneDeep(this.selectedEvent)

      if (event.media.length > 0 && copyMedia === null) {
        this.duplicateEventMediaDialog = true
        return
      }

      this.duplicateEventMediaDialog = false

      await this.processDuplicateEvent(event, copyMedia, true)
    },
    async processDuplicateEvent(event, copyMedia = false, selectEvent = false) {
      event.internal_id = this.getNewId()
      event.training_program_event_id = undefined
      event.id = null

      if (copyMedia) {
        for (const [index, media] of event.media.entries()) {
          MediaApiService.copy(media.id).then(result => {
            event.media.splice(index, 1, result.data)
          })
        }
      } else {
        event.media = []
      }

      this.events.push(event)

      if (selectEvent) this.selectedEventInternalIds = [event.internal_id]

      this.resortEvents()
    },
    removeEvent() {
      const index = this.events.findIndex(e => e.internal_id === this.selectedEventInternalId)

      this.events.splice(index, 1)

      if (this.events[index - 1]) {
        this.selectedEventInternalIds = [this.events[index - 1].internal_id]
      } else if (this.events[index]) {
        this.selectedEventInternalIds = [this.events[index].internal_id]
      } else {
        this.selectedEventInternalIds = []
      }
    },
    resortEvents() {
      this.events = this.events.slice().sort((a, b) => {
        const aTime = dayjs(a.start)
        const bTime = dayjs(b.start)

        if (aTime.isSame(bTime, 'minute')) return (a.internal_id > b.internal_id ? 1 : -1)
        else return aTime.isAfter(bTime) ? 1 : -1
      })
    },
    isEventValid(event) {
      return !(!event.title || !event.start_time || !event.end_time)
    },
    selectEvent(event, clickModifier = false) {
      if (clickModifier) {
        this.selectEventMultiple(event)

        return
      }

      this.selectedEventInternalIds = [event.internal_id]

      this.calendarDay = event.date

      setTimeout(() => {
        if (!this.isEventValid(event)) {
          this.$refs.trainingProgramEventForm.$refs.validation.validate()
        } else {
          this.$refs.trainingProgramEventForm.$refs.validation.reset()
        }
      }, 100)
    },
    selectEventMultiple(event) {
      const index = this.selectedEventInternalIds.indexOf(event.internal_id)

      if (index > -1 && this.selectedEventInternalIds.length > 1) {
        this.selectedEventInternalIds.splice(index, 1)
      } else if (index === -1) {
        this.selectedEventInternalIds.push(event.internal_id)
      }

      this.calendarDay = event.date

      if (this.selectedEventInternalIds.length === 1) {
        setTimeout(() => {
          if (!this.isEventValid(event)) {
            this.$refs.trainingProgramEventForm.$refs.validation.validate()
          } else {
            this.$refs.trainingProgramEventForm.$refs.validation.reset()
          }
        }, 100)
      }
    },
    applyToAllEvents(variable, value) {
      this.events = this.events.map(e => {
        if (variable === 'time') {
          const newStartAt = dayjs(e.date + dayjs(value.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))
          const newEndAt = dayjs(e.date + dayjs(value.end_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))

          e = {
            ...e,
            start_time: newStartAt.format('HH:mm'),
            start_at: newStartAt.toISOString(),
            start: newStartAt.toDate(),
            end_time: newEndAt.format('HH:mm'),
            end_at: newEndAt.toISOString(),
            end: newEndAt.toDate()
          }
        } else {
          e[variable] = value
        }

        return e
      })

      this.$store.dispatch(this.$storeActions.ALERT, {
        text: this.$t('training_programs.dialog.alerts.successfully_applied'),
        color: 'success'
      })
    },
    async fetchAllLocations() {
      try {
        this.launchEventsDialog.location.itemsLoading = true

        const result = await LocationApiService.getAll()

        this.launchEventsDialog.location.items = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_locations.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.launchEventsDialog.location.itemsLoading = true
      }
    },
    async fetchAllCoaches() {
      try {
        this.launchEventsDialog.coaches.loading = true

        const result = await CoachApiService.getPaginated({})

        this.launchEventsDialog.coaches.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.coach_get.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.launchEventsDialog.coaches.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.launchEventsDialog.players.loading = true

        const result = await PlayerApiService.getSimpleList({ order_by: 'last_name', order: 'ASC' })

        this.launchEventsDialog.players.data = result.data
      } finally {
        this.launchEventsDialog.players.loading = false
      }
    },
    async submitLaunchEventDialog() {
      const validation = await this.$refs.launchDialogValidation.validate()

      if (!validation) return

      this.startDate = this.launchEventsDialog.applyAll.start_date

      this.$nextTick(() => {
        this.events = this.events.map(e => {
          e.team_ids = this.launchEventsDialog.applyAll.team_ids
          e.player_ids = this.launchEventsDialog.applyAll.player_ids
          e.coach_ids = this.launchEventsDialog.applyAll.coach_ids
          if (this.launchEventsDialog.applyAll.location_id) e.location_id = this.launchEventsDialog.applyAll.location_id

          if (this.launchEventsDialog.applyStartTime && this.launchEventsDialog.applyAll.start_time) {
            const startAt = dayjs(e.start_at)
            const endAt = dayjs(e.end_at)
            const duration = endAt.diff(startAt, 'minute')

            const newStartAt = dayjs(e.date + ' ' + dayjs(this.launchEventsDialog.applyAll.start_time, getDateTimeFormatForUser('HH:mm')).format('HH:mm:ss'))
            const newEndAt = newStartAt.add(duration, 'minute')

            e = {
              ...e,
              start_time: newStartAt.format('HH:mm'),
              start_at: newStartAt.toISOString(),
              start: newStartAt.toDate(),
              end_time: newEndAt.format('HH:mm'),
              end_at: newEndAt.toISOString(),
              end: newEndAt.toDate()
            }
          }

          if (this.launchEventsDialog.applyTimezone && this.launchEventsDialog.applyAll.tz) {
            e = {
              ...e,
              tz: this.launchEventsDialog.applyAll.tz
            }
          }

          return e
        })

        this.launchEventsDialog.visible = false
      })
    },
    previousEvent() {
      if (!this.selectedEventInternalId) return

      const currentIndex = this.events.findIndex(e => e.internal_id === this.selectedEventInternalId)

      if (!(currentIndex > -1) || currentIndex === 0) return

      this.selectedEventInternalIds = [this.events[currentIndex - 1].internal_id]
    },
    nextEvent() {
      if (!this.selectedEventInternalId) return

      const currentIndex = this.events.findIndex(e => e.internal_id === this.selectedEventInternalId)

      if (!(currentIndex > -1) || currentIndex === (this.events.length - 1)) return

      this.selectedEventInternalIds = [this.events[currentIndex + 1].internal_id]
    },
    startDelete() {
      if (this.trainingProgramInstance.data) {
        this.deleteInstanceDialog.visible = true
        this.deleteInstanceDialog.mode = 'program'

        return
      }

      this.deleteConfirmationDialogVisible = true
    },
    async submitDeleteInstance() {
      if (!this.trainingProgramInstance.data) return

      this.deleteInstanceDialog.submitting = true

      try {
        await TrainingProgramApiService.deleteInstance(this.trainingProgramInstance.data.id, this.deleteInstanceDialog.mode)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('training_programs.dialog.alerts.successfully_deleted'),
          color: 'success'
        })

        EventBus.$emit(this.$events.TRAINING_PROGRAM_INSTANCE_DELETED, this.trainingProgramInstance.data)

        this.deleteInstanceDialog.visible = false
        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.something_went_wrong'),
          color: 'error'
        })

        throw e
      } finally {
        this.deleteInstanceDialog.submitting = false
      }
    },
    initCopyEvents() {
      if (this.selectedEventInternalIds.length <= 1) return

      const firstEvent = this.events.find(e => e.internal_id === this.selectedEventInternalIds[0])

      this.copyEventsDialog.newDate = firstEvent.date
      this.copyEventsDialog.newStartTime = firstEvent.start_time
      this.copyEventsDialog.copyMedia = true
      this.copyEventsDialog.visible = true
    },
    copyEvents() {
      for (const oldEvent of this.selectedEventsForCopy) {
        const event = cloneDeep(oldEvent)

        event.internal_id = this.getNewId()
        event.training_program_event_id = undefined
        event.id = null

        if (this.copyEventsDialog.copyMedia) {
          for (const [index, media] of event.media.entries()) {
            MediaApiService.copy(media.id).then(result => {
              event.media.splice(index, 1, result.data)
            })
          }
        } else {
          event.media = []
        }

        this.events.push(event)
      }

      this.calendarDay = this.selectedEventsForCopy[0].date

      this.copyEventsDialog.visible = false
      this.resortEvents()
    },
    async duplicateProgram() {
      if (this.editMode !== 'editProgram') return

      this.duplicatingProgram = true

      for (const event of this.events) {
        const newEvent = cloneDeep(event)

        const oldEventIndex = this.events.findIndex(e => e.internal_id === event.internal_id)

        this.events.splice(oldEventIndex, 1)

        await this.processDuplicateEvent(newEvent, true, false)
      }

      this.programFormData.id = null

      this.duplicatingProgram = false

      this.$store.dispatch(this.$storeActions.ALERT, {
        text: this.$t('training_programs.dialog.alerts.program_duplicated'),
        color: 'success'
      })
    }
  }
}
</script>
